import {
  ClinicalTrial,
  ClinicalTrialLocation,
  ClinicalTrialPhase,
  ClinicalTrialsDefaultsType,
  ClinicalTrialsFilterOptionsType,
  ClinicalTrialsSearchParams,
  ClinicalTrialsSearchResponse,
  ClinicalTrialSort,
} from './types';
import { FacetValue, FilterPanelType } from 'components/types/FilterTypes';

export const CLINICAL_TRIALS_API_BASE =
  'https://clinicaltrials.gov/api/v2/studies';

export const CLINICAL_TRIALS_STUDY_BASE = 'https://clinicaltrials.gov/study';

// Temporary catalog scope until Sitecore Study Id is populated.
export const DEFAULT_SPONSOR = 'OSF Healthcare';

export const PAGE_SIZE = 10;
export const MAX_PAGE_SIZE = 50;
export const MAX_STUDY_IDS = 100;
export const MAX_CANCER_TYPE_LABEL = 160;
export const MAX_SELECTED_CANCER_TYPES = 40;
export const NCT_ID_PATTERN = /^NCT\d{8}$/i;
const CONDITION_HARVEST_PAGE_SIZE = 1000;
const CONDITION_HARVEST_MAX_PAGES = 10;
const CANCER_TYPE_CACHE_MS = 5 * 60 * 1000;

export const filterDefaults: ClinicalTrialsDefaultsType = {
  acceptingnewpatients: [],
  cancertypes: [],
  phase: [],
  query: '',
  sort: 'BEST_MATCH',
  page: 1,
};

export const PHASE_OPTIONS: Array<{
  label: string;
  value: ClinicalTrialPhase;
}> = [
  { label: 'Early Phase 1', value: 'EARLY_PHASE1' },
  { label: 'Phase 1', value: 'PHASE1' },
  { label: 'Phase 2', value: 'PHASE2' },
  { label: 'Phase 3', value: 'PHASE3' },
  { label: 'Phase 4', value: 'PHASE4' },
  { label: 'Not Applicable', value: 'NA' },
];

export const filterPanelsDefaults: FilterPanelType[] = [
  { id: 'cancertypes', name: 'Types of Cancer', active: false },
  { id: 'phase', name: 'Phase', active: false },
];

export const toCancerTypeFacets = (types: string[]): FacetValue[] =>
  types.map(type => ({
    id: type.toLowerCase(),
    text: type,
    count: 0,
  }));

export const buildFilterOptions = (
  cancerTypes: string[] = []
): ClinicalTrialsFilterOptionsType => ({
  acceptingnewpatients: [{ id: 'true', text: 'true', count: 0 }],
  cancertypes: toCancerTypeFacets(cancerTypes),
  phase: PHASE_OPTIONS.map(option => ({
    id: option.value,
    text: option.label,
    count: 0,
  })),
});

const PHASE_LABEL_TO_API: Record<string, ClinicalTrialPhase> = {
  'Early Phase 1': 'EARLY_PHASE1',
  'Phase 1': 'PHASE1',
  'Phase 2': 'PHASE2',
  'Phase 3': 'PHASE3',
  'Phase 4': 'PHASE4',
  'Not Applicable': 'NA',
  EARLY_PHASE1: 'EARLY_PHASE1',
  PHASE1: 'PHASE1',
  PHASE2: 'PHASE2',
  PHASE3: 'PHASE3',
  PHASE4: 'PHASE4',
  NA: 'NA',
};

export const formatPhaseLabel = (phase?: string): string => {
  if (!phase) return '';
  const normalized = phase.toUpperCase().replace(/\s+/g, '_');
  const match = PHASE_OPTIONS.find(
    option =>
      option.value === normalized ||
      option.value === phase ||
      option.label === phase
  );
  if (match) return match.label;
  if (normalized === 'NA') return 'Not Applicable';
  return phase
    .replace(/_/g, ' ')
    .replace(/PHASE/i, 'Phase ')
    .replace(/\s+/g, ' ')
    .trim();
};

const isUnknownPhase = (phase?: string): boolean => {
  const normalized = (phase || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  return (
    !normalized ||
    normalized === 'NA' ||
    normalized === 'N/A' ||
    normalized === 'UNKNOWN' ||
    normalized === 'NOT_APPLICABLE'
  );
};

export const getPhaseBadgeLabels = (phases?: string[]): string[] => {
  const labels = (phases || [])
    .filter(phase => !isUnknownPhase(phase))
    .map(formatPhaseLabel)
    .filter(Boolean);
  return labels.length ? labels : ['Phase: unknown'];
};

export const getStatusBadgeLabel = (status?: string): string => {
  const normalized = (status || '').trim().toUpperCase().replace(/\s+/g, '_');
  if (!normalized || normalized === 'UNKNOWN') return 'Status: unknown';
  return formatOverallStatusLabel(status);
};

export const mapPhaseFiltersToApi = (
  phases: string[]
): ClinicalTrialPhase[] => {
  return phases
    .map(
      phase =>
        PHASE_LABEL_TO_API[phase] || PHASE_LABEL_TO_API[phase.toUpperCase()]
    )
    .filter(Boolean) as ClinicalTrialPhase[];
};

export const mapSortToApi = (sort: ClinicalTrialSort): string | undefined => {
  switch (sort) {
    case 'NEWEST':
      return 'LastUpdatePostDate:desc';
    case 'ALPHABETICAL':
      return 'BriefTitle:asc';
    case 'REVERSE_ALPHABETICAL':
      return 'BriefTitle:desc';
    case 'BEST_MATCH':
    default:
      return undefined;
  }
};

export const normalizeNctId = (value?: string): string => {
  const match = (value || '')
    .trim()
    .toUpperCase()
    .match(/NCT\d{8}/);
  return match ? match[0] : '';
};

export const formatOverallStatusLabel = (status?: string): string => {
  if (!status) return '';
  return status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());
};

export const formatLocationLine = (location: ClinicalTrialLocation): string => {
  const locality = [
    location.city,
    [location.state, location.zip].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');
  if (location.facility && locality)
    return `${location.facility} — ${locality}`;
  return location.facility || locality || '';
};

const eligibilityItems = (block: string): string[] =>
  block
    .split(/\n+/)
    .map(line => line.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, '').trim())
    .filter(
      line =>
        line.length > 0 &&
        !/^inclusion criteria:?$/i.test(line) &&
        !/^exclusion criteria:?$/i.test(line)
    );

export const parseEligibilityCriteria = (
  raw?: string
): { inclusion: string[]; exclusion: string[]; body: string } => {
  if (!raw?.trim()) return { inclusion: [], exclusion: [], body: '' };
  const text = raw.replace(/\r\n/g, '\n').trim();
  const hasInclusion = /inclusion criteria:?/i.test(text);
  const hasExclusion = /exclusion criteria:?/i.test(text);

  if (!hasInclusion && !hasExclusion) {
    return { inclusion: [], exclusion: [], body: text };
  }

  if (hasInclusion && hasExclusion) {
    const parts = text.split(/exclusion criteria:?/i);
    return {
      inclusion: eligibilityItems(
        (parts[0] || '').replace(/inclusion criteria:?/i, '')
      ),
      exclusion: eligibilityItems(parts.slice(1).join('\n')),
      body: '',
    };
  }

  if (hasInclusion) {
    return {
      inclusion: eligibilityItems(text.replace(/inclusion criteria:?/i, '')),
      exclusion: [],
      body: '',
    };
  }

  return {
    inclusion: [],
    exclusion: eligibilityItems(text.replace(/exclusion criteria:?/i, '')),
    body: '',
  };
};

export const parseStudyIds = (value?: string): string[] => {
  if (!value?.trim()) return [];
  const ids = value
    .split(/[,;\n]+/)
    .map(part => normalizeNctId(part))
    .filter(Boolean);
  return [...new Set(ids)].slice(0, MAX_STUDY_IDS);
};

const SORT_VALUES: ClinicalTrialSort[] = [
  'BEST_MATCH',
  'NEWEST',
  'ALPHABETICAL',
  'REVERSE_ALPHABETICAL',
];

export const sanitizeSearchParams = (
  input: Partial<ClinicalTrialsSearchParams> | undefined
): ClinicalTrialsSearchParams => {
  const body = input || {};
  const pageSize = Number(body.pageSize);
  const sort = SORT_VALUES.includes(body.sort as ClinicalTrialSort)
    ? (body.sort as ClinicalTrialSort)
    : 'BEST_MATCH';
  const cancerTypes = Array.isArray(body.cancerTypes)
    ? [
        ...new Set(
          body.cancerTypes
            .map(type => String(type).trim().slice(0, MAX_CANCER_TYPE_LABEL))
            .filter(
              type => type.length > 0 && !/AREA\[|query\.|filter\./i.test(type)
            )
        ),
      ].slice(0, MAX_SELECTED_CANCER_TYPES)
    : [];
  const studyIds = Array.isArray(body.studyIds)
    ? [
        ...new Set(
          body.studyIds.map(id => normalizeNctId(String(id))).filter(Boolean)
        ),
      ].slice(0, MAX_STUDY_IDS)
    : [];

  return {
    query:
      typeof body.query === 'string' ? body.query.trim().slice(0, 200) : '',
    cancerTypes,
    phases: Array.isArray(body.phases)
      ? body.phases.map(phase => String(phase)).slice(0, PHASE_OPTIONS.length)
      : [],
    acceptingNewPatients: body.acceptingNewPatients === true ? true : undefined,
    sort,
    pageSize:
      Number.isFinite(pageSize) && pageSize > 0
        ? Math.min(Math.floor(pageSize), MAX_PAGE_SIZE)
        : PAGE_SIZE,
    pageToken:
      typeof body.pageToken === 'string' && body.pageToken.trim()
        ? body.pageToken.trim()
        : undefined,
    sponsor:
      studyIds.length > 0
        ? typeof body.sponsor === 'string' && body.sponsor.trim()
          ? body.sponsor.trim().slice(0, 120)
          : undefined
        : typeof body.sponsor === 'string' && body.sponsor.trim()
          ? body.sponsor.trim().slice(0, 120)
          : DEFAULT_SPONSOR,
    studyIds,
  };
};

const fetchWithTimeout = async (
  url: string,
  timeoutMs = 15000
): Promise<Response> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

type Official = { name?: string; role?: string };
type Contact = {
  name?: string;
  role?: string;
  email?: string;
  phone?: string;
};
type ApiLocation = {
  facility?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
};
type StudyProtocol = {
  identificationModule?: {
    nctId?: string;
    briefTitle?: string;
    officialTitle?: string;
  };
  statusModule?: {
    overallStatus?: string;
    startDateStruct?: { date?: string };
  };
  descriptionModule?: {
    briefSummary?: string;
    detailedDescription?: string;
  };
  conditionsModule?: {
    conditions?: string[];
    keywords?: string[];
  };
  designModule?: {
    studyType?: string;
    phases?: string[];
  };
  eligibilityModule?: {
    eligibilityCriteria?: string;
    sex?: string;
    minimumAge?: string;
    maximumAge?: string;
  };
  armsInterventionsModule?: {
    interventions?: Array<{ name?: string }>;
  };
  sponsorCollaboratorsModule?: {
    leadSponsor?: { name?: string };
    responsibleParty?: {
      type?: string;
      investigatorFullName?: string;
    };
  };
  contactsLocationsModule?: {
    overallOfficials?: Official[];
    centralContacts?: Contact[];
    contacts?: Contact[];
    locations?: ApiLocation[];
  };
};

type ApiStudy = {
  protocolSection?: StudyProtocol;
};

const pickPrincipalInvestigator = (protocol?: StudyProtocol): string => {
  const responsibleParty =
    protocol?.sponsorCollaboratorsModule?.responsibleParty;
  if (responsibleParty?.type === 'PRINCIPAL_INVESTIGATOR') {
    return responsibleParty.investigatorFullName || '';
  }
  return '';
};

const pickOsfLocations = (
  locations: ApiLocation[] = []
): ClinicalTrialLocation[] =>
  locations.filter(location =>
    (location.facility || '').trim().toUpperCase().startsWith('OSF')
  );

const pickClinicalResearchCoordinator = (protocol?: StudyProtocol): string => {
  const central = protocol?.contactsLocationsModule?.centralContacts || [];
  if (central[0]?.name) return central[0].name;

  const contacts = protocol?.contactsLocationsModule?.contacts || [];
  const coordinator = contacts.find(contact =>
    /coordinator|contact/i.test(contact.role || '')
  );
  return coordinator?.name || contacts[0]?.name || '';
};

export const mapStudyToClinicalTrial = (study: ApiStudy): ClinicalTrial => {
  const protocol = study.protocolSection;
  const nctId = protocol?.identificationModule?.nctId || '';
  const overallStatus = protocol?.statusModule?.overallStatus || '';
  const phases = protocol?.designModule?.phases || [];
  const central = protocol?.contactsLocationsModule?.centralContacts || [];
  const otherContacts = protocol?.contactsLocationsModule?.contacts || [];

  return {
    nctId,
    briefTitle: protocol?.identificationModule?.briefTitle || '',
    officialTitle: protocol?.identificationModule?.officialTitle,
    overallStatus,
    phases,
    conditions: protocol?.conditionsModule?.conditions || [],
    keywords: protocol?.conditionsModule?.keywords || [],
    briefSummary: protocol?.descriptionModule?.briefSummary,
    detailedDescription: protocol?.descriptionModule?.detailedDescription,
    eligibilityCriteria: protocol?.eligibilityModule?.eligibilityCriteria,
    sex: protocol?.eligibilityModule?.sex,
    minimumAge: protocol?.eligibilityModule?.minimumAge,
    maximumAge: protocol?.eligibilityModule?.maximumAge,
    studyType: protocol?.designModule?.studyType,
    interventions: (protocol?.armsInterventionsModule?.interventions || [])
      .map(item => item.name)
      .filter((name): name is string => Boolean(name)),
    principalInvestigator: pickPrincipalInvestigator(protocol),
    clinicalResearchCoordinator: pickClinicalResearchCoordinator(protocol),
    leadSponsor: protocol?.sponsorCollaboratorsModule?.leadSponsor?.name,
    startDate: protocol?.statusModule?.startDateStruct?.date,
    acceptingNewPatients: overallStatus === 'RECRUITING',
    studyUrl: nctId ? `${CLINICAL_TRIALS_STUDY_BASE}/${nctId}` : '',
    locations: pickOsfLocations(
      protocol?.contactsLocationsModule?.locations || []
    ),
    contacts: [...central, ...otherContacts],
  };
};

export const getStudyByNctIdFromSource = async (
  nctId: string
): Promise<ClinicalTrial | null> => {
  const id = normalizeNctId(nctId);
  if (!id) return null;

  const response = await fetchWithTimeout(
    `${CLINICAL_TRIALS_API_BASE}/${encodeURIComponent(id)}?format=json`
  );

  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`ClinicalTrials.gov request failed (${response.status})`);
  }

  const data = await response.json();
  const study = data.protocolSection ? data : data.studies?.[0];
  if (!study) return null;

  return mapStudyToClinicalTrial(study);
};

const STUDY_LIST_FIELDS = [
  'NCTId',
  'BriefTitle',
  'OfficialTitle',
  'OverallStatus',
  'Phase',
  'Condition',
  'Keyword',
  'BriefSummary',
  'StudyType',
  'LocationFacility',
  'ResponsiblePartyType',
  'ResponsiblePartyInvestigatorFullName',
].join(',');

const quoteConditionQuery = (value: string): string => {
  const cleaned = value.replace(/"/g, '').trim();
  if (!cleaned) return '';
  return /\s/.test(cleaned) ? `"${cleaned}"` : cleaned;
};

export const uniqueConditionLabels = (labels: string[]): string[] => {
  const unique = new Map<string, string>();
  labels.forEach(condition => {
    const label = String(condition).trim().slice(0, MAX_CANCER_TYPE_LABEL);
    if (!label) return;
    const key = label.toLowerCase();
    if (!unique.has(key)) unique.set(key, label);
  });
  return [...unique.values()].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
};

export const uniqueConditions = (studies: ClinicalTrial[]): string[] =>
  uniqueConditionLabels(studies.flatMap(study => study.conditions || []));

export const buildSearchQueryString = (
  params: ClinicalTrialsSearchParams
): string => {
  const searchParams = new URLSearchParams();
  searchParams.set('format', 'json');
  searchParams.set('countTotal', 'true');
  searchParams.set('pageSize', String(params.pageSize || PAGE_SIZE));
  searchParams.set('fields', STUDY_LIST_FIELDS);

  if (params.studyIds && params.studyIds.length > 0) {
    searchParams.set('filter.ids', params.studyIds.join(','));
  } else if (params.sponsor) {
    searchParams.set('query.spons', params.sponsor);
  }

  if (params.query?.trim()) {
    searchParams.set('query.term', params.query.trim());
  }

  if (params.cancerTypes && params.cancerTypes.length > 0) {
    searchParams.set(
      'query.cond',
      params.cancerTypes.map(quoteConditionQuery).filter(Boolean).join(' OR ')
    );
  }

  if (params.acceptingNewPatients) {
    searchParams.set('filter.overallStatus', 'RECRUITING');
  }

  const apiPhases = mapPhaseFiltersToApi(params.phases || []);
  if (apiPhases.length > 0) {
    const phaseQuery = apiPhases
      .map(phase => `AREA[Phase]${phase}`)
      .join(' OR ');
    searchParams.set(
      'filter.advanced',
      apiPhases.length > 1 ? `(${phaseQuery})` : phaseQuery
    );
  }

  const sort = mapSortToApi(params.sort || 'BEST_MATCH');
  if (sort) {
    searchParams.set('sort', sort);
  }

  if (params.pageToken) {
    searchParams.set('pageToken', params.pageToken);
  }

  return searchParams.toString();
};

const cancerTypeCache = new Map<string, { expires: number; types: string[] }>();

const cancerTypeCacheKey = (params: ClinicalTrialsSearchParams): string =>
  params.studyIds && params.studyIds.length > 0
    ? `ids:${[...params.studyIds].sort().join(',')}`
    : `spons:${params.sponsor || DEFAULT_SPONSOR}`;

const buildCancerTypeHarvestQueryString = (
  params: ClinicalTrialsSearchParams,
  pageToken?: string
): string => {
  const searchParams = new URLSearchParams();
  searchParams.set('format', 'json');
  searchParams.set('pageSize', String(CONDITION_HARVEST_PAGE_SIZE));
  searchParams.set('fields', STUDY_LIST_FIELDS);

  if (params.studyIds && params.studyIds.length > 0) {
    searchParams.set('filter.ids', params.studyIds.join(','));
  } else if (params.sponsor) {
    searchParams.set('query.spons', params.sponsor);
  }

  if (pageToken) searchParams.set('pageToken', pageToken);
  return searchParams.toString();
};

export const collectCancerTypesFromSource = async (
  params: Pick<ClinicalTrialsSearchParams, 'sponsor' | 'studyIds'>
): Promise<string[]> => {
  const sanitized = sanitizeSearchParams({
    sponsor: params.sponsor,
    studyIds: params.studyIds,
  });
  const cacheKey = cancerTypeCacheKey(sanitized);
  const cached = cancerTypeCache.get(cacheKey);
  if (cached && cached.expires > Date.now()) {
    return cached.types;
  }

  const unique = new Map<string, string>();
  let pageToken: string | undefined;

  for (let page = 0; page < CONDITION_HARVEST_MAX_PAGES; page++) {
    const response = await fetchWithTimeout(
      `${CLINICAL_TRIALS_API_BASE}?${buildCancerTypeHarvestQueryString(
        sanitized,
        pageToken
      )}`
    );

    if (!response.ok) {
      throw new Error(`ClinicalTrials.gov request failed (${response.status})`);
    }

    const data = await response.json();
    const studies = Array.isArray(data.studies)
      ? data.studies.map(mapStudyToClinicalTrial)
      : [];

    uniqueConditions(studies).forEach(label => {
      const key = label.toLowerCase();
      if (!unique.has(key)) unique.set(key, label);
    });

    pageToken =
      typeof data.nextPageToken === 'string' && data.nextPageToken
        ? data.nextPageToken
        : undefined;
    if (!pageToken) break;
  }

  const types = [...unique.values()].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
  cancerTypeCache.set(cacheKey, {
    expires: Date.now() + CANCER_TYPE_CACHE_MS,
    types,
  });
  return types;
};

export const searchClinicalTrialsFromSource = async (
  params: ClinicalTrialsSearchParams
): Promise<ClinicalTrialsSearchResponse> => {
  const queryString = buildSearchQueryString(sanitizeSearchParams(params));
  const response = await fetchWithTimeout(
    `${CLINICAL_TRIALS_API_BASE}?${queryString}`
  );

  if (!response.ok) {
    throw new Error(`ClinicalTrials.gov request failed (${response.status})`);
  }

  const data = await response.json();
  const studies = Array.isArray(data.studies)
    ? data.studies.map(mapStudyToClinicalTrial)
    : [];

  return {
    studies,
    totalCount: typeof data.totalCount === 'number' ? data.totalCount : 0,
    nextPageToken:
      typeof data.nextPageToken === 'string' && data.nextPageToken
        ? data.nextPageToken
        : undefined,
  };
};

export const getConditionBadgeLabel = (conditions: string[]): string => {
  if (!conditions?.length) return '';
  const first = conditions[0];
  return first.length > 28 ? `${first.slice(0, 25)}…` : first;
};

export const getResultsCountLabel = (
  count: number,
  page: number,
  pageSize: number
): string => {
  if (count === 0) return 'Showing 0 Clinical Trials';
  if (count <= pageSize) {
    return `Showing ${count} of ${count} Clinical Trials`;
  }
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, count);
  return `Showing ${start}-${end} of ${count} Clinical Trials`;
};

export const emptyFacets = (): Record<string, FacetValue[]> => ({
  acceptingnewpatients: [],
  cancertypes: [],
  phase: [],
});
